import React, { useState } from 'react';
import './ReportModal.css';

export interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  title: string;
  itemName: string;
  itemType: 'track' | 'feedback';
}

const REPORT_REASONS = [
  { id: 'copyright', label: 'Copyright infringement', description: 'This content uses copyrighted material without permission' },
  { id: 'hate', label: 'Hate speech or discrimination', description: 'Content that promotes hate or discrimination' },
  { id: 'violence', label: 'Promotes violence', description: 'Content that promotes or glorifies violence' },
  { id: 'spam', label: 'Spam or misleading', description: 'Content that is spam, fake, or misleading' },
  { id: 'harassment', label: 'Harassment or bullying', description: 'Content that harasses or bullies others' },
  { id: 'inappropriate', label: 'Inappropriate content', description: 'Content that is sexually explicit or otherwise inappropriate' },
  { id: 'other', label: 'Other', description: 'Another reason not listed above' },
];

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  itemName,
  itemType,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedReason) {
      setError('Please select a reason for your report');
      return;
    }

    const selectedReasonObj = REPORT_REASONS.find(r => r.id === selectedReason);
    let fullReason = selectedReasonObj?.label || selectedReason;
    
    if (additionalDetails.trim()) {
      fullReason += `: ${additionalDetails.trim()}`;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(fullReason);
      setSuccess(true);
      // Auto-close after showing success message
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setAdditionalDetails('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="report-modal-overlay" onClick={handleClose}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-modal-header">
          <h2>{title}</h2>
          <button 
            className="report-modal-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {success ? (
          <div className="report-modal-content">
            <div className="report-success">
              <span className="report-success-icon">✓</span>
              <h3>Report Submitted</h3>
              <p>Thank you for helping keep our community safe. We will review your report and take appropriate action.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="report-modal-content">
            <p className="report-description">
              You are reporting <strong>"{itemName}"</strong>. Please select a reason for your report:
            </p>

            <div className="report-reasons">
              {REPORT_REASONS.map((reason) => (
                <label key={reason.id} className="report-reason-option">
                  <input
                    type="radio"
                    name="reportReason"
                    value={reason.id}
                    checked={selectedReason === reason.id}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <div className="report-reason-content">
                    <span className="report-reason-label">{reason.label}</span>
                    <span className="report-reason-description">{reason.description}</span>
                  </div>
                </label>
              ))}
            </div>

            <div className="report-additional-details">
              <label htmlFor="additionalDetails">
                Additional details (optional)
              </label>
              <textarea
                id="additionalDetails"
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder="Provide any additional context that might help us review this report..."
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
              />
              <span className="character-count">
                {additionalDetails.length}/500
              </span>
            </div>

            {error && (
              <div className="report-error">
                {error}
              </div>
            )}

            <div className="report-modal-actions">
              <button
                type="button"
                className="report-cancel-btn"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="report-submit-btn"
                disabled={isSubmitting || !selectedReason}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
